import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DynamicString,
  TOnboardingConfig,
  TVerificationAppFieldConfigItem,
} from "../../models";
import { ConditionEditorDialog } from "./ConditionEditorDialog";
import { ConditionPreview } from "./ConditionPreview";
import { createSimpleCondition } from "./conditionHelpers";
import {
  DynamicStringConditionBranch,
  getDuplicateDynamicStringTexts,
  getNextDynamicStringBranchText,
  parseDynamicString,
  serializeDynamicString,
} from "./dynamicStringHelpers";
import { InputActionMenu } from "./InputActionMenu";

const DUPLICATE_TEXT_HELPER = "Each branch text must be unique.";

type DynamicStringProperty = "label" | "subtitle";

type DraftBranch = DynamicStringConditionBranch & {
  id: string;
};

export const DynamicStringEditor: FC<{
  availableFields: TVerificationAppFieldConfigItem[];
  config: TOnboardingConfig;
  fieldIndex: number;
  property: DynamicStringProperty;
  stepIndex: number;
  title: string;
  value: DynamicString;
  onChange: (nextValue: string | DynamicString | undefined) => void;
}> = memo(({
  availableFields,
  config,
  fieldIndex,
  property,
  stepIndex,
  title,
  value,
  onChange,
}) => {
  const parsedValue = useMemo(() => parseDynamicString(value), [value]);
  const nextIdRef = useRef(0);
  const lastPropSignatureRef = useRef<string | null>(null);
  const [editingBranchId, setEditingBranchId] = useState<string | null>(null);

  const buildDraftBranches = useCallback(
    (branches: DynamicStringConditionBranch[]): DraftBranch[] => branches.map((branch) => ({
      ...branch,
      id: `dynamic-string-branch-${nextIdRef.current++}`,
    })),
    [],
  );

  const [defaultText, setDefaultText] = useState(parsedValue?.defaultText ?? "");
  const [branches, setBranches] = useState<DraftBranch[]>(
    () => buildDraftBranches(parsedValue?.branches ?? []),
  );

  const propSignature = useMemo(
    () => parsedValue ? JSON.stringify(parsedValue) : null,
    [parsedValue],
  );
  const draftSignature = useMemo(
    () => JSON.stringify({
      defaultText,
      branches: branches.map(({ condition, text }) => ({ condition, text })),
    }),
    [branches, defaultText],
  );

  useEffect(() => {
    if (!propSignature || !parsedValue) {
      return;
    }

    if (lastPropSignatureRef.current === propSignature) {
      return;
    }

    lastPropSignatureRef.current = propSignature;

    if (propSignature === draftSignature) {
      return;
    }

    setDefaultText(parsedValue.defaultText);
    setBranches(buildDraftBranches(parsedValue.branches));
  }, [buildDraftBranches, draftSignature, parsedValue, propSignature]);

  const duplicateTexts = useMemo(
    () => getDuplicateDynamicStringTexts(defaultText, branches),
    [branches, defaultText],
  );
  const editingBranch = useMemo(
    () => branches.find((branch) => branch.id === editingBranchId) ?? null,
    [branches, editingBranchId],
  );

  const normalizeStaticValue = useCallback(
    (nextText: string): string | undefined => {
      if (property === "subtitle") {
        return nextText || undefined;
      }

      return nextText;
    },
    [property],
  );

  const persistIfValid = useCallback(
    (nextDefaultText: string, nextBranches: DraftBranch[]) => {
      if (getDuplicateDynamicStringTexts(nextDefaultText, nextBranches).size > 0) {
        return;
      }

      onChange(serializeDynamicString(
        nextDefaultText,
        nextBranches.map(({ condition, text }) => ({ condition, text })),
      ));
    },
    [onChange],
  );

  const updateDefaultText = useCallback(
    (nextText: string) => {
      setDefaultText(nextText);
      persistIfValid(nextText, branches);
    },
    [branches, persistIfValid],
  );

  const updateBranchText = useCallback(
    (branchId: string, nextText: string) => {
      const nextBranches = branches.map((branch) =>
        branch.id === branchId ? { ...branch, text: nextText } : branch,
      );

      setBranches(nextBranches);
      persistIfValid(defaultText, nextBranches);
    },
    [branches, defaultText, persistIfValid],
  );

  const addBranch = useCallback(() => {
    const nextBranches = [
      ...branches,
      {
        id: `dynamic-string-branch-${nextIdRef.current++}`,
        text: getNextDynamicStringBranchText(
          defaultText,
          branches.map((branch) => branch.text),
        ),
        condition: createSimpleCondition(availableFields),
      },
    ];

    setBranches(nextBranches);
    persistIfValid(defaultText, nextBranches);
  }, [availableFields, branches, defaultText, persistIfValid]);

  const removeBranch = useCallback(
    (branchId: string) => {
      const nextBranches = branches.filter((branch) => branch.id !== branchId);

      setBranches(nextBranches);

      if (nextBranches.length === 0) {
        onChange(normalizeStaticValue(defaultText));
        return;
      }

      persistIfValid(defaultText, nextBranches);
    },
    [branches, defaultText, normalizeStaticValue, onChange, persistIfValid],
  );

  const removeDynamicBlock = useCallback(
    () => {
      onChange(normalizeStaticValue(defaultText));
    },
    [defaultText, normalizeStaticValue, onChange],
  );
  const defaultActions = useMemo(
    () => [
      {
        label: "Make static",
        onClick: removeDynamicBlock,
      },
    ],
    [removeDynamicBlock],
  );

  const saveBranchCondition = useCallback(
    (nextCondition: DynamicStringConditionBranch["condition"]) => {
      if (!editingBranchId) {
        return;
      }

      const nextBranches = branches.map((branch) =>
        branch.id === editingBranchId
          ? { ...branch, condition: nextCondition }
          : branch,
      );

      setBranches(nextBranches);
      persistIfValid(defaultText, nextBranches);
      setEditingBranchId(null);
    },
    [branches, defaultText, editingBranchId, persistIfValid],
  );

  if (!parsedValue) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
        }}
      >
        <Stack spacing={1.25}>
          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <TextField
            label='Default'
            value={defaultText}
            onChange={(event) => updateDefaultText(event.target.value)}
            error={duplicateTexts.has(defaultText)}
            helperText={duplicateTexts.has(defaultText) ? DUPLICATE_TEXT_HELPER : undefined}
            size='small'
            fullWidth
            InputProps={{
              endAdornment: (
                <InputActionMenu
                  actions={defaultActions}
                  label={title}
                />
              ),
            }}
          />

          {branches.map((branch, index) => (
            <Stack key={branch.id} spacing={1}>
              <TextField
                label='Alternative value'
                value={branch.text}
                onChange={(event) => updateBranchText(branch.id, event.target.value)}
                error={duplicateTexts.has(branch.text)}
                helperText={duplicateTexts.has(branch.text) ? DUPLICATE_TEXT_HELPER : undefined}
                size='small'
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label={`Remove ${title.toLowerCase()} alternative value ${index + 1}`}
                        edge='end'
                        size='small'
                        onClick={() => removeBranch(branch.id)}
                        sx={{
                          color: "text.secondary",
                          "&:hover": {
                            color: "error.main",
                            bgcolor: "action.hover",
                          },
                        }}
                      >
                        <DeleteOutlineIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <ConditionPreview
                availableFields={availableFields}
                condition={branch.condition}
                onEdit={() => setEditingBranchId(branch.id)}
                title='When'
              />
            </Stack>
          ))}

          <Button size='small' startIcon={<AddIcon />} onClick={addBranch} sx={{ alignSelf: "flex-start" }}>
            Add value
          </Button>
        </Stack>
      </Box>

      {editingBranch ? (
        <ConditionEditorDialog
          availableFields={availableFields}
          condition={editingBranch.condition}
          fieldIndex={fieldIndex}
          open
          stepIndex={stepIndex}
          title={`Edit ${title.toLowerCase()} condition`}
          validationConfig={config}
          validationPath={`/elements/${stepIndex}/fields/${fieldIndex}/${property}`}
          onClose={() => setEditingBranchId(null)}
          onSave={saveBranchCondition}
        />
      ) : null}
    </>
  );
});
